<script>
	import {
		ChartContext,
		DataTooltip,
		Html,
		KeyboardSelector,
		Rows,
		Svg,
		TooltipState,
	} from '@vpap_svelte/charts';
	import { AppContext } from './Context.svelte';
	import { format, scaleBand, scaleLinear } from '@vpap_svelte/charts/d3';

	let ctx = AppContext.get();
	let dims = ChartContext.get();

	let yScale = $derived.by(() => {
		return scaleBand()
			.domain(ctx.percentsByDonorType.map((d) => d[0]))
			.range([0, dims.chart.height])
			.padding(0.1);
	});
	let xScale = $derived.by(() => {
		return scaleLinear().domain([0, 1]).range([0, dims.chart.width]);
	});

	const colors = ['#399F9F', '#F6895A', '#714489', '#FFC000'];

	let tooltip = new TooltipState();
</script>

<Html class="pointer-none">
	<KeyboardSelector
		data={ctx.percentsByDonorType}
		type="flat"
		orientation="vertical"
		onSelectionChange={(record) => {
			if (record === undefined) {
				tooltip.hide();
				tooltip.data = null;
				ctx.tipChart = undefined;
			} else {
				tooltip.mouseover(record.selected);
				ctx.tipChart = 'stackedbar';
				const box = dims.chartElement.getBoundingClientRect();
				tooltip.y = yScale(record.selected[0]) + box.top;
				tooltip.x = box.left;
			}
		}}
	></KeyboardSelector>
</Html>
<Svg class="pointer-none">
	<Rows data={ctx.percentsByDonorType} yScale={{ scale: yScale, accessor: (d) => d[0] }}>
		{#snippet children(datum)}
			{@const [governor, amounts] = datum.datum}
			{@const govSpaceSplit = governor.split(' ')}
			{@const lastName = govSpaceSplit[govSpaceSplit.length - 1]}
			<text fill="#42585c" x={-5} y={yScale.bandwidth() / 2 + 4} text-anchor="end">{lastName}</text>
			{#each amounts as amountRange, idx}
				<rect
					style:pointer-events="none !important"
					fill={colors[idx]}
					stroke="#42585c"
					y={0}
					height={yScale.bandwidth()}
					x={xScale(amountRange[0])}
					width={xScale(amountRange[1]) - xScale(amountRange[0])}
				></rect>
			{/each}
		{/snippet}
	</Rows>
</Svg>

<Html class="pointer-none">
	{#if tooltip.data && ctx.tipChart === 'stackedbar'}
		<DataTooltip
			id="g-tooltip"
			x={tooltip.x + 275 > dims.chart.width ? tooltip.x - 275 : tooltip.x}
			y={tooltip.y}
			bind:visible={tooltip.visible}
			position={ctx.isMobile ? 'fixed-bottom' : 'fixed'}
			style={ctx.isMobile
				? { width: '100%', 'z-index': 2, 'pointer-events': 'all' }
				: { width: '275px', 'z-index': 2 }}
		>
			{@const donorAmountMap = new Map(ctx.byDonorAndType.get(tooltip.data[0]))}
			<div class="tip-head">{tooltip.data[0]}</div>
			<div class="tip-content">
				<table class="tip-table">
					<thead>
						<tr>
							<th>Donor Type</th>
							<th>Amount</th>
							<th style="text-align: right">%</th>
						</tr>
					</thead>
					<tbody>
						{#each ctx.colorMapping as [donorType, color], idx}
							<tr>
								<td>
									<div class="circle-grouping">
										<div class="filled-circle" style:background-color={color}></div>
										<div>{donorType}</div>
									</div>
								</td>
								<td>{format('$,.0f')(donorAmountMap.get(donorType))}</td>
								<td style="text-align:right"
									>{format('.1%')(tooltip.data[1][idx][1] - tooltip.data[1][idx][0])}</td
								>
							</tr>
						{/each}
					</tbody>
				</table>
			</div>
		</DataTooltip>
	{/if}
</Html>
<Svg class="pointer-all">
	<Rows data={ctx.percentsByDonorType} yScale={{ scale: yScale, accessor: (d) => d[0] }}>
		{#snippet children(datum)}
			<rect
				x={0}
				width={dims.chart.width}
				y={0}
				height={yScale.bandwidth()}
				class="tip-empty pointer-all"
				onmouseover={() => {
					tooltip.mouseover(datum.datum);
					ctx.tipChart = 'stackedbar';
				}}
				onmousemove={(e) => {
					tooltip.mousemove(e);
				}}
				onmouseleave={() => {
					tooltip.hide();
					ctx.tipChart = undefined;
				}}
				fill="none"
			></rect>
		{/snippet}
	</Rows>
</Svg>

<style>
	.tip-head {
		font-size: 1rem;
		text-align: center;
		margin: 0.3em;
		border-bottom: 1px solid rgb(100, 100, 100);
	}

	.tip-content {
		font-size: 0.9rem;
	}

	.tip-table {
		width: calc(100% - 1em);
		margin: auto;
	}

	.tip-table thead {
		font-size: 0.9rem;
		text-transform: uppercase;
	}

	.tip-table thead th {
		font-weight: normal;
	}

	.circle-grouping {
		display: flex;
		align-items: center;
	}

	.filled-circle {
		height: 10px;
		width: 10px;
		border-radius: 50%;
		margin-right: 0.2em;
	}

	text {
		fill: #42585c;
	}
</style>
